import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLanguageContext } from '../../../../../contexts/LanguageContext';
import Dialog from '../../../../../components/Dialog';
import { ActionButton } from '../../../../../components/Button';
import { Grid } from '@mui/material';
import DefaultConstants from '../../../../../data/Constants';
import { IDefaultDialogFilterProps } from '../../../../../interfaces/IReport';
import DateTimeHelper from '../../../../../helpers/DateTimeHelper';
import DateField from '../../../../../components/DateField';
import SelectField from '../../../../../components/SelectField';
import { useUserContext } from '../../../../../contexts/UserContext';
import IEnterprise from '../../../../../interfaces/IEnterprise';
import { IBlock, IFloor, IRoom, IRoomType } from '../../../../../interfaces/IPartnerPms';
import IUser from '../../../../../interfaces/IUser';
import { GovernanceStatusData, IDefaultStatusData, RoomStatusData, RoomStatusEnum } from '../../../../../data/Enums';


export interface IRoomLogReportFilters {
    enterprise: IEnterprise | undefined;
    room: IRoom[];
    user: IUser[];
    block: IBlock[];
    floor: IFloor[];
    roomType: IRoomType[];
    startDate: string;
    endDate: string;
    initialStatus: IDefaultStatusData[];
    finalStatus: IDefaultStatusData[];
}


const initialFilters: IRoomLogReportFilters = {
    enterprise: undefined,
    room: [],
    user: [],
    block: [],
    floor: [],
    roomType: [],
    startDate: DateTimeHelper.Now('YYYY-MM-DD'),
    endDate: DateTimeHelper.Now('YYYY-MM-DD'), 
    initialStatus: [], 
    finalStatus: []
};



interface IRoomLogReportDialogFilterProps extends IDefaultDialogFilterProps {
    userList: IUser[];
}

const RoomLogFilters: React.FunctionComponent<IRoomLogReportDialogFilterProps> = ({
    open, 
    handleClose,
    handleOpenPreview,
    enterpriseList,
    userList
}) => {
    const { translate } = useLanguageContext();
    const { enterprise } = useUserContext();
    
    const [filters, setFilters] = useState<IRoomLogReportFilters>({
        ...initialFilters,
        enterprise: enterprise
    });
    const [startDate, setStartDate] = useState<string | undefined>(initialFilters.startDate);
    const [endDate, setEndDate] = useState<string | undefined>(initialFilters.endDate);


    useEffect(() => {
        if (open) {
            setFilters({
                ...initialFilters, 
                enterprise: enterprise
            });
            setStartDate(initialFilters.startDate);
            setEndDate(initialFilters.endDate);
        }

        // eslint-disable-next-line
    }, [open]);


    useEffect(() => {
        setFilters({
            ...filters,
            room: [],
            user: []
        });

        // eslint-disable-next-line
    }, [filters.enterprise]);


    const handleChangeFilterValue = useCallback((name: string, value: any) => {
        setFilters({
            ...filters,
            [name]: value
        });
        // eslint-disable-next-line
    }, [filters]);


    const enterprisePartnerPms = useMemo(() => {
        return {
            rooms: filters.enterprise?.partnerPms.rooms ?? [],
            users: userList.filter(u => u.enterprises.map(e => e.id).includes(filters.enterprise?.id ?? 0)) ?? [],
            blocks: filters.enterprise?.partnerPms.blocks ?? [],
            floors: filters.enterprise?.partnerPms.floors ?? [],
            roomTypes: filters.enterprise?.partnerPms.roomTypes ?? [],
        }
    }, [filters.enterprise, userList]);


    const roomLogPossibleStatus = useMemo(() => {
        const doNotDisturb = RoomStatusData[RoomStatusEnum.DoNotDisturb];

        return [
            ...Object.values(GovernanceStatusData).filter(x => x.partnerId),
            
            {
                ...doNotDisturb,
                key: 998,
                name: `${doNotDisturb.name}Enabled`,
                partnerId: 'true'
            }, {
                ...doNotDisturb,
                key: 999,
                name: `${doNotDisturb.name}Disabled`,
                partnerId: 'false'
            } 
        ];
    }, []);


    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='close' onClick={handleClose} />,
            <ActionButton action='view' type='submit' />
        ];
        // eslint-disable-next-line
    }, [translate]);


    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const _filters = {
            ...filters,
            startDate: startDate,
            endDate: endDate
        };

        if (startDate && endDate && startDate > endDate) {
            const aux = startDate;
            _filters.startDate = endDate;
            _filters.endDate = aux;
        }

        handleOpenPreview(_filters);

        setStartDate(_filters.startDate);
        setEndDate(_filters.endDate);
        // eslint-disable-next-line
    }, [filters, startDate, endDate]);


    return (
        <Dialog 
            open={open}
            handleClose={handleClose}
            title={translate('roomLog')}
            fullWidth={true}
            dialogActions={dialogActions}
            onSubmit={handleSubmit}
            className='dialogactions-spacebetween'
            maxWidth='md'
            fullHeight={true}
        >
            <Grid container rowSpacing={DefaultConstants.gridRowSpacing} columnSpacing={DefaultConstants.gridColumnSpacing}>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                    <SelectField
                        label={translate('enterprise')}
                        name='enterprise'
                        value={filters.enterprise?.id ?? 0}
                        onChange={e => {
                            const { name, value } = e.target;
                            const enterprise = enterpriseList.find(e => +e.id === +value);
                            handleChangeFilterValue(name, enterprise);
                        }}
                        required={true}
                        data={enterpriseList.map(e => {
                            return {
                                value: +e.id,
                                text: e.name
                            };
                        })}
                    />
                </Grid>

                <Grid item lg={3} md={3} sm={6} xs={6}>
                    <DateField
                        label={translate('startDate')}
                        format='DD/MM/YYYY'
                        name='startDate'
                        value={DateTimeHelper.ToDate(startDate)}
                        onChange={value => setStartDate(DateTimeHelper.Format(value, 'YYYY-MM-DD'))}
                        required={true}
                        disableFuture={true}
                    />
                </Grid>

                <Grid item lg={3} md={3} sm={6} xs={6}>
                    <DateField
                        label={translate('endDate')}
                        format='DD/MM/YYYY'
                        name='endDate'
                        value={DateTimeHelper.ToDate(endDate)}
                        onChange={value => setEndDate(DateTimeHelper.Format(value, 'YYYY-MM-DD'))}
                        required={true}
                        disableFuture={true}
                    />
                </Grid>

                <Grid item xs={12} />

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('initialStatus')}
                        name='initialStatus'
                        value={filters.initialStatus.length > 0 ? filters.initialStatus.map(s => +s.key) : [0]}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const status = filters.initialStatus.length > 0 && values.includes(0) ? [] : roomLogPossibleStatus.filter(s => values.includes(+s.key));
                            handleChangeFilterValue(name, status);
                        }}
                        required={false}
                        multiple={true}
                        data={[{ value: 0, text: translate('all') }].concat(roomLogPossibleStatus.map(s => {
                            return {
                                value: +s.key,
                                text: translate(s.name)
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('finalStatus')}
                        name='finalStatus'
                        value={filters.finalStatus.length > 0 ? filters.finalStatus.map(s => +s.key) : [0]}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const status = filters.finalStatus.length > 0 && values.includes(0) ? [] : roomLogPossibleStatus.filter(s => values.includes(+s.key));
                            handleChangeFilterValue(name, status);
                        }}
                        required={false}
                        multiple={true}
                        data={[{ value: 0, text: translate('all') }].concat(roomLogPossibleStatus.map(s => {
                            return {
                                value: +s.key,
                                text: translate(s.name)
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('user')}
                        name='user'
                        value={filters.user.length > 0 ? filters.user.map(r => +r.id) : [0]}
                        required={false}
                        multiple={true}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const user = filters.user.length > 0 && values.includes(0) ? [] : enterprisePartnerPms.users.filter(r => values.includes(+r.id));
                            handleChangeFilterValue(name, user);
                        }}
                        data={[{ value: 0, text: translate('all') }].concat(enterprisePartnerPms.users.map(r => {
                            return {
                                value: +r.id,
                                text: r.login
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('room')}
                        name='room'
                        value={filters.room.length > 0 ? filters.room.map(r => +r.id) : [0]}
                        required={false}
                        multiple={true}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const room = filters.room.length > 0 && values.includes(0) ? [] : enterprisePartnerPms.rooms.filter(r => values.includes(+r.id));
                            handleChangeFilterValue(name, room);
                        }}
                        data={[{ value: 0, text: translate('all') }].concat(enterprisePartnerPms.rooms.map(r => {
                            return {
                                value: +r.id,
                                text: r.description
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('block')}
                        name='block'
                        value={filters.block.length > 0 ? filters.block.map(b => +b.id) : [0]}
                        required={false}
                        multiple={true}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const block = filters.block.length > 0 && values.includes(0) ? [] : enterprisePartnerPms.blocks.filter(b => values.includes(+b.id));
                            handleChangeFilterValue(name, block);
                        }}
                        data={[{ value: 0, text: translate('all') }].concat(enterprisePartnerPms.blocks.map(b => {
                            return {
                                value: +b.id,
                                text: b.description
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('floor')}
                        name='floor'
                        value={filters.floor.length > 0 ? filters.floor.map(f => +f.id) : [0]}
                        required={false}
                        multiple={true}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const floor = filters.floor.length > 0 && values.includes(0) ? [] : enterprisePartnerPms.floors.filter(f => values.includes(+f.id));
                            handleChangeFilterValue(name, floor);
                        }}
                        data={[{ value: 0, text: translate('all') }].concat(enterprisePartnerPms.floors.map(f => {
                            return {
                                value: +f.id,
                                text: f.description
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('roomType')}
                        name='roomType'
                        value={filters.roomType.length > 0 ? filters.roomType.map(r => +r.id) : [0]}
                        required={false}
                        multiple={true}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const roomType = filters.roomType.length > 0 && values.includes(0) ? [] : enterprisePartnerPms.roomTypes.filter(r => values.includes(+r.id));
                            handleChangeFilterValue(name, roomType);
                        }}
                        data={[{ value: 0, text: translate('all') }].concat(enterprisePartnerPms.roomTypes.map(r => {
                            return {
                                value: +r.id,
                                text: r.description
                            };
                        }))}
                    />
                </Grid>
            </Grid>
        </Dialog>
    );
}

export default RoomLogFilters;